import { useEffect } from 'react';

// Android paints its status bar with <meta name="theme-color">; the page cannot
// draw under it. So when a sheet dims the page (bg-black/60), the bar has to be
// dimmed by hand too, or it stays a bright strip above the darkened screen.
//
// Student screens set the base colour (StudentAmbientBackground); open sheets
// register themselves with useStatusBarDim. Several can be open at once (a
// sheet opened from another): their backdrops stack, and so does the dimming.
// With no base colour — coach side — nothing is touched.

// Same strength as the sheets' backdrop: black at 60 %.
const DIM_ALPHA = 0.6;

let baseColor = null;
// Share of light each open backdrop lets through (1 - its black alpha).
const openDimmers = [];

// #rrggbb seen through black at `alpha`.
export const dimHex = (hex, alpha = DIM_ALPHA) => {
  const n = parseInt(hex.slice(1), 16);
  const channel = (shift) => Math.round(((n >> shift) & 0xff) * (1 - alpha));
  return `#${[16, 8, 0].map((s) => channel(s).toString(16).padStart(2, '0')).join('')}`;
};

// iOS does not read theme-color for an installed app: with
// `black-translucent` it paints the bar with the BODY's background (and no
// longer draws the page under it since iOS 26.1). The body's default grey
// gradient then showed as a #1f1f1f strip above the student screens. So the
// same colour also goes on <html>/<body>, through --kaiylo-status-bar (see
// src/index.css); the page itself is fully covered by its own background.
export const STATUS_BAR_VAR = '--kaiylo-status-bar';

const paintBody = (color) => {
  const root = document.documentElement;
  if (color) {
    root.style.setProperty(STATUS_BAR_VAR, color);
    root.setAttribute('data-status-bar', '');
  } else {
    root.style.removeProperty(STATUS_BAR_VAR);
    root.removeAttribute('data-status-bar');
  }
};

const apply = () => {
  if (!baseColor) {
    paintBody(null);
    return;
  }
  // Stacked backdrops multiply: 85 % then 60 % leaves 0.15 × 0.4 of the light.
  const light = openDimmers.reduce((acc, through) => acc * through, 1);
  const color = openDimmers.length > 0 ? dimHex(baseColor, 1 - light) : baseColor;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', color);
  paintBody(color);
};

// `null` hands the bar back: the caller restores its own default.
export const setStatusBarBase = (color) => {
  baseColor = color;
  apply();
};

// Puts index.html's default colour (`data-default`, the coach side's) back,
// unless a screen owns the bar (a base colour is set). Called when the boot
// splash goes away: the launch script painted the bar with the splash's own
// colour, which a coach screen would otherwise keep.
export const restoreStatusBarDefault = () => {
  if (baseColor) return;
  const meta = document.querySelector('meta[name="theme-color"]');
  const fallback = meta?.getAttribute('data-default');
  if (meta && fallback != null) meta.setAttribute('content', fallback);
  paintBody(null);
};

// Dims the status bar while `active` (a surface with a dark backdrop is
// open). `alpha` is that backdrop's black: 0.6 for the sheets, more for a
// full-screen panel such as the exercise modal.
export const useStatusBarDim = (active, alpha = DIM_ALPHA) => {
  useEffect(() => {
    if (!active) return undefined;
    const through = 1 - alpha;
    openDimmers.push(through);
    apply();
    return () => {
      openDimmers.splice(openDimmers.indexOf(through), 1);
      apply();
    };
  }, [active, alpha]);
};
